import { SsoConnection } from '@choco-admin/types';
import { formatDateWithPreset } from '@choco-admin/utils';
import type { SortDescriptor } from '@heroui/react';
import { Chip, TableCell, TableRow } from '@heroui/react';
import React, { type Key, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FiKey } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { ErrorCard } from '../../../../components/ErrorCard';
import { PageHeader } from '../../../../components/PageHeader';
import {
  type LoadingTableColumn,
  LoadingTable,
} from '../../../../components/Table';
import { useSsoConnections } from '../../hooks';
import { connectionTypeLabel } from '../../utils';

const columns: LoadingTableColumn[] = [
  {
    label: 'Connection',
    key: 'name',
    allowsSorting: true,
    width: '30%',
    className: 'pl-6',
  },
  { label: 'Type', width: '10%' },
  { label: 'Email domains', width: '24%', className: 'hidden md:table-cell' },
  {
    label: 'Single sign-out',
    width: '12%',
    className: 'hidden lg:table-cell',
  },
  {
    label: 'Created',
    key: 'createdAt',
    allowsSorting: true,
    width: '12%',
    className: 'hidden lg:table-cell',
  },
  {
    label: 'Last updated',
    key: 'updatedAt',
    allowsSorting: true,
    width: '12%',
    className: 'hidden lg:table-cell pr-6',
  },
];

type SortableConnectionColumn = 'name' | 'createdAt' | 'updatedAt';

const SORTABLE_COLUMNS: SortableConnectionColumn[] = [
  'name',
  'createdAt',
  'updatedAt',
];

const toSortableColumn = (column?: Key): SortableConnectionColumn =>
  SORTABLE_COLUMNS.find((candidate) => candidate === column) ?? 'name';

const sortValue = (
  connection: SsoConnection,
  column: SortableConnectionColumn
) => {
  if (column === 'name') {
    return connection.name.toLowerCase();
  }
  // Dates are ISO 8601, so a lexical compare matches chronological order.
  return connection[column] ?? '';
};

const formatDate = (value?: string): string =>
  value ? formatDateWithPreset(value, 'DAY_FIRST_MEDIUM') : '—';

export const ConnectionList: React.FC = () => {
  const navigate = useNavigate();
  const { connections, isLoading, error, refetch } = useSsoConnections();
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'name',
    direction: 'ascending',
  });

  const visibleConnections = useMemo(() => {
    const column = toSortableColumn(sortDescriptor.column);
    const factor = sortDescriptor.direction === 'descending' ? -1 : 1;

    return [...connections].sort(
      (a, b) =>
        sortValue(a, column).localeCompare(sortValue(b, column)) * factor
    );
  }, [connections, sortDescriptor]);

  if (error) {
    return (
      <div className="p-6">
        <ErrorCard
          title="Could not load SSO connections"
          message={error.message}
          onRetry={refetch}
        />
      </div>
    );
  }

  const renderRow = (connection: SsoConnection) => (
    <TableRow
      key={connection.id}
      className="cursor-pointer hover:bg-content2"
      onClick={() => navigate(`/sso/${encodeURIComponent(connection.id)}`)}
    >
      <TableCell className="pl-6 font-medium text-foreground">
        {connection.name}
      </TableCell>
      <TableCell>
        <Chip size="sm" variant="flat" color="secondary">
          {connectionTypeLabel(connection.type)}
        </Chip>
      </TableCell>
      <TableCell className="hidden md:table-cell break-words text-default-500">
        {connection.emailDomains.length > 0
          ? connection.emailDomains.join(', ')
          : '—'}
      </TableCell>
      <TableCell className="hidden lg:table-cell text-default-500">
        {connection.signOutEnabled ? 'Enabled' : 'Disabled'}
      </TableCell>
      <TableCell className="hidden lg:table-cell text-default-500">
        {formatDate(connection.createdAt)}
      </TableCell>
      <TableCell className="hidden lg:table-cell pr-6 text-default-500">
        {formatDate(connection.updatedAt)}
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-6">
      <Helmet>
        <title>SSO Connections - Choco Admin</title>
      </Helmet>
      <PageHeader
        title="SSO connections"
        description="Identity providers configured on the Choco user pool."
      />
      <LoadingTable<SsoConnection>
        columns={columns}
        ariaLabel="SSO connections"
        isLoading={isLoading}
        isEmpty={!isLoading && visibleConnections.length === 0}
        emptyState={{
          icon: FiKey,
          title: 'No SSO connections',
          description: 'No identity providers are configured on this pool.',
        }}
        data={visibleConnections}
        sortDescriptor={sortDescriptor}
        onSortChange={setSortDescriptor}
        renderRow={renderRow}
      />
    </div>
  );
};
